import { useState, useRef, useCallback, useEffect } from 'react'
import { Mic, Square, Play, Pause, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { documentService } from '@/services'
import { useLegacyUpdatesStream } from '@/hooks/useLegacyUpdatesStream'
import type { LegacyUpdateEvent } from '@/hooks/useLegacyUpdatesStream'

interface AudioRecordViewProps {
  onComplete: () => void
}

type RecordState = 'idle' | 'recording' | 'recorded' | 'uploading' | 'processing' | 'completed' | 'error'

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

export function AudioRecordView({ onComplete }: AudioRecordViewProps) {
  const [state, setState] = useState<RecordState>('idle')
  const [error, setError] = useState<string | null>(null)
  const [documentId, setDocumentId] = useState<string | null>(null)
  const [elapsed, setElapsed] = useState(0)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const fileRef = useRef<File | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const completionTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop()
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop())
      }
      if (timerRef.current) clearInterval(timerRef.current)
      if (audioUrl) URL.revokeObjectURL(audioUrl)
      if (completionTimer.current) clearTimeout(completionTimer.current)
    }
  }, [])

  // Request microphone permission on mount
  useEffect(() => {
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        // Permission granted – stop tracks immediately (we only needed the permission)
        stream.getTracks().forEach((t) => t.stop())
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'NotAllowedError') {
          setError('Microphone access denied. Please allow microphone permissions and try again.')
          setState('error')
        }
      })
  }, [])

  // SSE processing listener
  useLegacyUpdatesStream({
    entityTypes: ['document'],
    entityIds: documentId ? [documentId] : undefined,
    enabled: state === 'processing' && !!documentId,
    onUpdate: useCallback(
      (event: LegacyUpdateEvent) => {
        if (event.entityId !== documentId) return

        if (event.status === 'completed') {
          setState('completed')
          completionTimer.current = setTimeout(onComplete, 1500)
        } else if (event.status === 'failed') {
          setState('error')
          setError('Processing failed. Please try again.')
        }
      },
      [documentId, onComplete]
    ),
  })

  const startRecording = async () => {
    setError(null)
    chunksRef.current = []

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      const mediaRecorder = new MediaRecorder(stream)
      mediaRecorderRef.current = mediaRecorder

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' })
        const file = new File([blob], 'voice-note.webm', { type: 'audio/webm' })
        fileRef.current = file

        const url = URL.createObjectURL(blob)
        if (audioUrl) URL.revokeObjectURL(audioUrl)
        setAudioUrl(url)
        setState('recorded')

        // Stop all tracks after recording
        stream.getTracks().forEach((t) => t.stop())
        streamRef.current = null
      }

      mediaRecorder.start()
      setState('recording')
      setElapsed(0)
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000)
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setError('Microphone access denied. Please allow microphone permissions and try again.')
      } else {
        setError('Could not access microphone. Please check your device settings.')
      }
      setState('error')
    }
  }

  const stopRecording = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop()
    }
  }

  const togglePlayback = () => {
    if (!audioRef.current || !audioUrl) return

    if (isPlaying) {
      audioRef.current.pause()
      setIsPlaying(false)
    } else {
      audioRef.current.play()
      setIsPlaying(true)
    }
  }

  const handleUpload = async () => {
    if (!fileRef.current) return

    setState('uploading')
    setError(null)

    try {
      const res = await documentService.uploadDocument(fileRef.current, 'audio')

      if (res.documentId) {
        // Set documentId first, then transition to processing state
        // This ensures the documentId is available when useLegacyUpdatesStream enables
        setDocumentId(res.documentId)
        // Use setTimeout to ensure documentId state update completes first
        setTimeout(() => setState('processing'), 0)
      } else {
        setState('completed')
        completionTimer.current = setTimeout(onComplete, 1500)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed. Please try again.'
      setError(message)
      setState('error')
    }
  }

  const handleRetry = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl)
    setAudioUrl(null)
    setIsPlaying(false)
    fileRef.current = null
    setState('idle')
    setError(null)
    setDocumentId(null)
    setElapsed(0)
  }

  return (
    <Card className="flex flex-col border-none bg-card/75 backdrop-blur-sm shadow-2xl overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-white/5 bg-black/20">
        <div className="flex items-center gap-3 mb-1">
          <div className="h-8 w-8 rounded-lg bg-primary/20 flex items-center justify-center text-primary">
            <Mic className="h-4 w-4" />
          </div>
          <h2 className="text-lg font-semibold text-white">
            Record Your Message
          </h2>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Record a voice message describing your startup. We'll transcribe and
          extract key context automatically.
        </p>
      </div>

      {/* Content */}
      <div className="p-6 flex-1">
        {state === 'idle' && (
          <div className="flex flex-col items-center justify-center py-8 gap-6">
            {error && (
              <p className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2 w-full text-center">
                {error}
              </p>
            )}
            <button
              onClick={startRecording}
              aria-label="Start recording"
              className="h-20 w-20 rounded-full bg-primary/20 hover:bg-primary/30 flex items-center justify-center text-primary transition-colors"
            >
              <Mic className="h-8 w-8" />
            </button>
            <p className="text-sm text-muted-foreground">
              Tap to start recording
            </p>
          </div>
        )}

        {state === 'recording' && (
          <div className="flex flex-col items-center justify-center py-8 gap-6">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-destructive/20 animate-ping" />
              <button
                onClick={stopRecording}
                aria-label="Stop recording"
                className="relative h-20 w-20 rounded-full bg-destructive/20 hover:bg-destructive/30 flex items-center justify-center text-destructive transition-colors"
              >
                <Square className="h-6 w-6" />
              </button>
            </div>
            <div className="text-center">
              <p className="text-2xl font-mono text-foreground tabular-nums">
                {formatTime(elapsed)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Recording... tap to stop
              </p>
            </div>
          </div>
        )}

        {state === 'recorded' && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50 border border-white/5">
              <button
                onClick={togglePlayback}
                className="h-10 w-10 rounded-full bg-primary/20 hover:bg-primary/30 flex items-center justify-center text-primary shrink-0 transition-colors"
              >
                {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground">Voice recording</p>
                <p className="text-xs text-muted-foreground">
                  {formatTime(elapsed)}
                </p>
              </div>
            </div>

            {audioUrl && (
              <audio
                ref={audioRef}
                src={audioUrl}
                onEnded={() => setIsPlaying(false)}
                className="hidden"
              />
            )}

            <div className="flex gap-2">
              <Button
                variant="ghost"
                className="flex-1 h-12 hover:bg-secondary hover:text-white"
                onClick={handleRetry}
              >
                Re-record
              </Button>
              <Button
                className="flex-1 h-12 text-base bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20"
                onClick={handleUpload}
              >
                Upload & Process
              </Button>
            </div>
          </div>
        )}

        {state === 'uploading' && (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <Loader2 className="h-10 w-10 text-primary animate-spin" />
            <p className="text-sm text-foreground font-medium">Uploading recording...</p>
          </div>
        )}

        {state === 'processing' && (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <Loader2 className="h-10 w-10 text-primary animate-spin" />
            <p className="text-sm text-foreground font-medium">
              Processing your recording...
            </p>
            <p className="text-xs text-muted-foreground text-center">
              Transcribing audio, extracting entities, and building your context graph.
              This may take a moment.
            </p>
          </div>
        )}

        {state === 'completed' && (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <CheckCircle className="h-10 w-10 text-green-400" />
            <p className="text-sm text-foreground font-medium">
              Recording processed successfully!
            </p>
            <p className="text-xs text-muted-foreground">
              Proceeding to next step...
            </p>
          </div>
        )}

        {state === 'error' && (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <AlertCircle className="h-10 w-10 text-destructive" />
            <p className="text-sm text-destructive text-center">{error}</p>
            <Button
              variant="ghost"
              onClick={handleRetry}
              className="hover:bg-secondary hover:text-white"
            >
              Try again
            </Button>
          </div>
        )}
      </div>
    </Card>
  )
}
